import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  ModeEdit,
  RemoveCircleOutline
} from '@mui/icons-material';
import axios from 'axios';
import useAxios from 'axios-hooks';

import { useAuth } from 'hooks/use-auth';
import {
  Color,
  isValidUrlPathComponentString
} from 'utils';
import {
  Condition, parseConditions,
  Status, parseStatuses, encodeStatuses, StatusItem, makeStatusItems, getConditionFromStatus
} from 'models';
import Dialog, { ButtonType } from 'components/Dialog';


function makeSelectOptionKey(conditionId: string, count: number = 1) {
  return `${conditionId}:${count}`;
}


export default function Statuses() {
  const auth = useAuth();
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [statusItems, setStatusItems] = useState<StatusItem[]>([]);
  const [targetItem, setTargetItem] = useState<StatusItem>();
  const [openEditItem, setOpenEditItem] = useState(false);
  const [openRemoveItem, setOpenRemoveItem] = useState(false);

  const [{data: rawStatuses, loading, error}, refetch] = useAxios({
    url: `/v0/factories/${auth.factoryId}/statuses`,
    headers: auth.authorizationHeader
  });

  useEffect(() => {
    rawStatuses && setStatuses(parseStatuses(rawStatuses));
  }, [rawStatuses]);

  useEffect(() => {
    setStatusItems(makeStatusItems(statuses));
  }, [statuses]);

  function makeConditionsLabel(conditions: Status[]): string {
    return conditions?.reduce((acc, c, index) => {
      const condition = getConditionFromStatus(c)
      let name = `${condition.name}`
      if (c.countMax == 0) {
        name += '(未検出)';
      }
      return index > 0 ? `${acc}|${name}` : name;
    }, '');
  }

  function handleClickNew() {
    setTargetItem(undefined);
    setOpenEditItem(true);
  }

  function handleClickEdit(item: StatusItem) {
    setTargetItem(item);
    setOpenEditItem(true);
  }

  function handleClickRemove(item: StatusItem) {
    setTargetItem(item);
    setOpenRemoveItem(true);
  }

  function handleClickRemoveItem() {
    if (targetItem) {
      axios.delete(`/v0/factories/${auth.factoryId}/statuses`, {
        headers: auth.authorizationHeader,
        data: {
          factory_id: targetItem.factoryId,
          id: targetItem.id
        }
      }).then((res) => {
        alert('状況定義を削除しました');
      }).catch((err) => {
        alert('状況定義の削除に失敗しました');
      }).finally(() => {
        setOpenRemoveItem(false);
        refetch();
      });
    }
  }

  return (
    <div>
      <Button
        variant="contained"
        onClick={handleClickNew}
      >新規作成</Button>
      <EditItemModal
        open={openEditItem}
        targetItem={targetItem}
        onClose={() => setOpenEditItem(false)}
        onSubmitSuccessfully={() => { setOpenEditItem(false); refetch(); }}
      />
      <Dialog
        open={openRemoveItem}
        buttonType={ButtonType.YesNo}
        onClose={() => setOpenRemoveItem(false)}
        onClickYes={handleClickRemoveItem}
      >
        次の状況定義を削除してもよろしいですか？<br/>
        状況ID: {targetItem?.id}<br/>
        状況名: {targetItem?.name}
      </Dialog>
      {loading ? (
        <p>Loading...</p>
      ) : (error ? (
        <p>Error!!</p>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">状況ID</TableCell>
                <TableCell align="left">状況名</TableCell>
                <TableCell>モニタ</TableCell>
                <TableCell>ステッカー</TableCell>
                <TableCell>信号灯</TableCell>
                <TableCell>ドア</TableCell>
                <TableCell>人間</TableCell>
                <TableCell>色</TableCell>
                <TableCell>稼働率カウント</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statusItems.map((item: StatusItem) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{makeConditionsLabel(item.conditions['monitor'])}</TableCell>
                  <TableCell>{makeConditionsLabel(item.conditions['sticker'])}</TableCell>
                  <TableCell>{makeConditionsLabel(item.conditions['light'])}</TableCell>
                  <TableCell>{makeConditionsLabel(item.conditions['door'])}</TableCell>
                  <TableCell>{makeConditionsLabel(item.conditions['person'])}</TableCell>
                  <TableCell>
                    <Box
                      width={30}
                      height={30}
                      bgcolor={item.colorRgb?.toString()}
                      sx={{border: '1px solid #000'}}
                    />
                  </TableCell>
                  <TableCell>{item.isCountTarget && 'ON' || 'OFF'}</TableCell>
                  <TableCell>
                    <IconButton color="inherit" onClick={() => handleClickEdit(item)}>
                      <ModeEdit />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton color="error" onClick={() => handleClickRemove(item)}>
                      <RemoveCircleOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------------------
// Modal to create new item or edit item

interface EditItemModalProps {
  open: boolean;
  targetItem?: StatusItem;
  onClose?: () => void;
  onSubmitFailed?: () => void;
  onSubmitSuccessfully?: () => void;
}

function EditItemModal({
  open,
  targetItem,
  onClose = () => {},
  onSubmitFailed = () => {},
  onSubmitSuccessfully = () => {}
}: EditItemModalProps) {
  const auth = useAuth();
  const defaultColorCode = '#ffffff';
  const [isEdit, setIsEdit] = useState(!!targetItem);
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [colorCode, setColorCode] = useState(defaultColorCode);
  const [selectOptions, setSelectOptions] = useState<{[key: string]: Status}>();
  const [selectedMonitorStatuses, setSelectedMonitorStatuses] = useState<string[]>([]);
  const [selectedStickerStatuses, setSelectedStickerStatuses] = useState<string[]>([]);
  const [selectedLightStatuses, setSelectedLightStatuses] = useState<string[]>([]);
  const [selectedDoorStatuses, setSelectedDoorStatuses] = useState<string[]>([]);
  const [selectedPersonStatuses, setSelectedPersonStatuses] = useState<string[]>([]);
  const [optionsMonitorStatuses, setOptionsMonitorStatuses] = useState<Status[]>([]);
  const [optionsStickerStatuses, setOptionsStickerStatuses] = useState<Status[]>([]);
  const [optionsLightStatuses, setOptionsLightStatuses] = useState<Status[]>([]);
  const [optionsDoorStatuses, setOptionsDoorStatuses] = useState<Status[]>([]);
  const [optionsPersonStatuses, setOptionsPersonStatuses] = useState<Status[]>([]);
  const [isCountTarget, setIsCountTarget] = useState(true);
  const [idHelperText, setIdHelperText] = useState('');

  const [{data: rawConditions, loading, error}, refetch] = useAxios({
    url: `/v0/conditions`,
    headers: auth.authorizationHeader
  });

  useEffect(() => {
    const setSelectedOptions = (conditions?: Status[]) => {
      return conditions?.map(a => makeSelectOptionKey(a.conditionId, a.countMax)) || [];
    };
    setIsEdit(!!targetItem);
    setId(targetItem?.id || '');
    setName(targetItem?.name || '');
    setColorCode(targetItem?.colorRgb?.toHexString() || defaultColorCode);
    setSelectedMonitorStatuses(setSelectedOptions(targetItem?.conditions['monitor']));
    setSelectedStickerStatuses(setSelectedOptions(targetItem?.conditions['sticker']));
    setSelectedLightStatuses(setSelectedOptions(targetItem?.conditions['light']));
    setSelectedDoorStatuses(setSelectedOptions(targetItem?.conditions['door']));
    setSelectedPersonStatuses(setSelectedOptions(targetItem?.conditions['person']));
    if (targetItem?.isCountTarget === undefined) {
      setIsCountTarget(true);
    } else {
      setIsCountTarget(!!targetItem.isCountTarget);
    }
  }, [targetItem]);

  useEffect(() => {
    if (rawConditions) {
      let statusOpts = parseConditions(rawConditions).map(a => ({
        id: id,
        name: name,
        countMin: 1,
        countMax: 1,
        objectId: a.objectId,
        objectName: a.objectName,
        conditionId: a.id,
        conditionName: a.name,
      }));
      // 取得したconditionsをモニタ・ステッカー・信号灯に分離
      setOptionsMonitorStatuses(statusOpts.filter(
        (a: any) => a.objectId === 'monitor'
      ));
      setOptionsStickerStatuses(statusOpts.filter(
        (a: any) => a.objectId === 'sticker'
      ));
      setOptionsLightStatuses(statusOpts.filter(
        (a: any) => a.objectId === 'light'
      ));
      setOptionsDoorStatuses(statusOpts.filter(
        (a: any) => a.objectId === 'door'
      ));

      let personOpts = statusOpts.filter(
        (a: any) => a.objectId === 'person'
      );
      if (personOpts.length) {
        statusOpts.push({
          ...personOpts[0],
          countMin: 0,
          countMax: 0,
          conditionName: '人間（未検出）'
        });
      }
      setOptionsPersonStatuses(statusOpts.filter(
        (a: any) => a.objectId === 'person'
      ));

      setSelectOptions(statusOpts.reduce((acc, item) => {
        acc[makeSelectOptionKey(item.conditionId, item.countMax)] = item;
        return acc;
      }, {} as {[key: string]: Status}));
    }
  }, [rawConditions]);

  useEffect(() => {
    if (!id || isValidUrlPathComponentString(id)) {
      setIdHelperText('');
    } else {
      setIdHelperText('利用できない文字列が含まれています');
    }
  }, [id]);

  function isReadyToSubmit(): boolean {
    // id, nameは必須. 条件はいずれか１つ以上必要
    return !!(id && name && (
      selectedMonitorStatuses.length ||
      selectedStickerStatuses.length ||
      selectedLightStatuses.length ||
      selectedDoorStatuses.length ||
      selectedPersonStatuses.length
    ));
  }

  function makeStatuses(optionKeys: string[], orGroupId: number): Status[] {
    // 実際にDBに格納されるデータ形式に変形
    return optionKeys.map(a => ({
      ...(selectOptions && selectOptions[a]),
      id: id,
      name: name,
      orGroupId: orGroupId,
      colorRgb: new Color(colorCode),
      isCountTarget: isCountTarget,
      isEnabled: true,
    } as Status));
  }

  function handleChangeId(e: any) {
    setId(e.target.value);
  }

  function handleChangeSelectedMonitorStatuses(e: SelectChangeEvent<string[]>) {
    const value = e.target.value;
    setSelectedMonitorStatuses(
      typeof value === 'string' ? value.split(',') : value
    );
  }
  function handleChangeSelectedStickerStatuses(e: SelectChangeEvent<string[]>) {
    const value = e.target.value;
    setSelectedStickerStatuses(
      typeof value === 'string' ? value.split(',') : value
    );
  }
  function handleChangeSelectedLightStatuses(e: SelectChangeEvent<string[]>) {
    const value = e.target.value;
    setSelectedLightStatuses(
      typeof value === 'string' ? value.split(',') : value
    );
  }
  function handleChangeSelectedDoorStatuses(e: SelectChangeEvent<string[]>) {
    const value = e.target.value;
    setSelectedDoorStatuses(
      typeof value === 'string' ? value.split(',') : value
    );
  }
  function handleChangeSelectedPersonStatuses(e: SelectChangeEvent<string[]>) {
    const value = e.target.value;
    setSelectedPersonStatuses(
      typeof value === 'string' ? value.split(',') : value
    );
  }

  function handleChangeColorCode(e: any) {
    setColorCode(e.target.value);
  }

  function handleChangeIsCountTarget(e: any) {
    setIsCountTarget(!isCountTarget);
  }

  function handleClickOk() {
    const statuses = encodeStatuses(Array.prototype.concat(
      makeStatuses(selectedMonitorStatuses, 0),
      makeStatuses(selectedStickerStatuses, 1),
      makeStatuses(selectedLightStatuses, 2),
      makeStatuses(selectedDoorStatuses, 3),
      makeStatuses(selectedPersonStatuses, 4)
    ));
    axios.post(`/v0/factories/${auth.factoryId}/statuses`, statuses, {
      headers: auth.authorizationHeader
    }).then((res) => {
      onSubmitSuccessfully();
    }).catch((err) => {
      alert('状況定義の登録に失敗しました');
      onSubmitFailed();
    });
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        border: '1px solid #000',
        py: 1, px: 2,
        m: '10px auto',
        width: '80%'
      }}>
          <Typography variant={"h6"}>
            {isEdit ? '状況定義編集' : '状況定義新規追加'}
          </Typography>
          <TextField
            label="状況ID"
            variant="outlined"
            value={id}
            required
            disabled={isEdit}
            error={!!idHelperText}
            helperText={idHelperText}
            onChange={handleChangeId}
          />
          <TextField
            label="状況名"
            variant="outlined"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
          <SelectStatuses
            label="モニタ"
            value={selectedMonitorStatuses}
            options={optionsMonitorStatuses}
            onChange={handleChangeSelectedMonitorStatuses}
          />
          <SelectStatuses
            label="ステッカー"
            value={selectedStickerStatuses}
            options={optionsStickerStatuses}
            onChange={handleChangeSelectedStickerStatuses}
          />
          <SelectStatuses
            label="ライト"
            value={selectedLightStatuses}
            options={optionsLightStatuses}
            onChange={handleChangeSelectedLightStatuses}
          />
          <SelectStatuses
            label="ドア"
            value={selectedDoorStatuses}
            options={optionsDoorStatuses}
            onChange={handleChangeSelectedDoorStatuses}
          />
          <SelectStatuses
            label="人間"
            value={selectedPersonStatuses}
            options={optionsPersonStatuses}
            onChange={handleChangeSelectedPersonStatuses}
            multiple={false}
          />
          <Box display="flex" sx={{mt: 1}}>
            <TextField
              label="色"
              variant="outlined"
              value={colorCode}
              onChange={handleChangeColorCode}
              inputProps={{maxLength: 7}}
            />
            <Box width={50} height={50} bgcolor={colorCode} sx={{border: '1px solid #000'}} />
          </Box>
          <FormControlLabel
            control={
              <Switch checked={isCountTarget} onChange={handleChangeIsCountTarget} />
            }
            label="稼働率カウント対象"
          />
          <Button
            type="submit"
            color="primary"
            variant="contained"
            fullWidth
            disabled={!isReadyToSubmit()}
            onClick={handleClickOk}
            sx={{mt: 1}}
          >
            OK
          </Button>
      </Box>
    </Modal>
  );
}

// --------------------------------------------------------------------------------------
// Modal select element

interface ModalSelectProps {
  label: string;
  value: string[];
  options: Status[];
  onChange: (e: SelectChangeEvent<string[]>) => void;
  multiple?: boolean;
}

function SelectStatuses({label, value, options, onChange, multiple = true}: ModalSelectProps) {
  const handleOnChange = (e: SelectChangeEvent<string[]>) => {
    if (!multiple && e.target.value.length > 1) {
      // multiple=falseの場合は最後に選択された項目のみを保持する新しいイベントを作成
      const newValue = e.target.value.slice(-1);
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: newValue,
        },
      } as SelectChangeEvent<string[]>;
      onChange(newEvent);
    } else {
      onChange(e);
    }
  };
  return (
    <FormControl fullWidth variant="outlined" sx={{mt: 1}}>
      <InputLabel>{label}</InputLabel>
      <Select multiple label={label} value={value} onChange={handleOnChange}>
      {options.map((a: Status) => {
        const c = getConditionFromStatus(a);
        const key = makeSelectOptionKey(c.id, a.countMax);
        return (<MenuItem key={key} value={key}>{c.name}</MenuItem>);
      })}
      </Select>
    </FormControl>
  );
}

